import { Plus, Trash } from '@phosphor-icons/react';
import { Button, IconButton, Inline, Stack, TextInput } from '@reelplicate/ui';
import type { ReactNode } from 'react';
import {
  type Control,
  Controller,
  useFieldArray,
  type UseFormSetValue,
} from 'react-hook-form';

import { PlatformSelect } from '@/features/accounts/platform-select';

import {
  type AccountValues,
  blankAccount,
  type CompetitorsFormValues,
  linkPlatform,
} from './competitor-form';

/**
 * One competitor's accounts: a platform picker and "@handle or profile link"
 * per row (pasting a link picks its platform), "+ Add account", and an
 * optional status line under each.
 */
export function AccountsEditor({
  control,
  setValue,
  index,
  name,
  canAdd,
  status,
  size = 'md',
}: {
  control: Control<CompetitorsFormValues>;
  setValue: UseFormSetValue<CompetitorsFormValues>;
  /** Which competitor in the form. */
  index: number;
  /** What to call the competitor in labels, e.g. "Competitor 2". */
  name: string;
  /** False at the plan's limit. */
  canAdd: boolean;
  status?: (account: AccountValues) => ReactNode;
  size?: 'sm' | 'md';
}) {
  const { fields, append, remove } = useFieldArray({
    control,
    name: `competitors.${index}.accounts`,
  });

  return (
    <Stack gap={2}>
      <ul aria-label={`${name} accounts`} className="flex flex-col gap-2">
        {fields.map((field, i) => {
          const label = `${name} account ${i + 1}`;
          return (
            <li key={field.id}>
              <Controller
                name={`competitors.${index}.accounts.${i}`}
                control={control}
                render={({ field: { value }, fieldState }) => {
                  const error = (fieldState.error as { handle?: { message?: string } })
                    ?.handle?.message;
                  return (
                    <Stack gap={1}>
                      <Inline gap={2} alignY="center">
                        <PlatformSelect
                          size={size}
                          value={value.platform}
                          onValueChange={(platform) =>
                            setValue(
                              `competitors.${index}.accounts.${i}.platform`,
                              platform,
                              {
                                shouldDirty: true,
                              },
                            )
                          }
                          label={`${label} platform`}
                        />
                        <TextInput
                          size={size}
                          className="min-w-0 flex-1"
                          autoComplete="off"
                          aria-label={label}
                          aria-invalid={Boolean(error)}
                          placeholder="@handle or profile link"
                          value={value.handle}
                          onChange={(event) => {
                            const next = event.target.value;
                            setValue(`competitors.${index}.accounts.${i}.handle`, next, {
                              shouldDirty: true,
                            });
                            const platform = linkPlatform(next);
                            if (platform && platform !== value.platform) {
                              setValue(
                                `competitors.${index}.accounts.${i}.platform`,
                                platform,
                                { shouldDirty: true },
                              );
                            }
                          }}
                        />
                        <IconButton
                          type="button"
                          icon={Trash}
                          variant="ghost"
                          size={size}
                          aria-label={`Remove ${label}`}
                          onClick={() => remove(i)}
                        />
                      </Inline>
                      {error && (
                        <p className="text-caption text-status-danger-fg">{error}</p>
                      )}
                      {status?.(value)}
                    </Stack>
                  );
                }}
              />
            </li>
          );
        })}
      </ul>
      <Inline>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          leadingIcon={Plus}
          disabled={!canAdd}
          onClick={() => append(blankAccount(), { shouldFocus: false })}
        >
          Add account
        </Button>
      </Inline>
    </Stack>
  );
}
